import React, { useState, useEffect } from "react";
import { Stethoscope, Heart } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/api/apiConfig";
import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";
import {
  type Patient,
  type VisitReason,
  reasonMap,
} from "../../../types";
import type { MedicalConstant, ExtractedPatientData } from "../../../types";

interface PatientInfoStepProps {
  constanteData?: MedicalConstant;
}

const PatientInfoStep: React.FC<PatientInfoStepProps> = ({
  constanteData,
}) => {
  const { formData, updateFormData } = useConsultationWizardState(constanteData);
  const [patientDetails, setPatientDetails] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(false);

  const info = formData.patientInfo;

  // Récupération patient si besoin
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (
        constanteData?.patientId &&
        !constanteData.patient &&
        !patientDetails
      ) {
        setLoading(true);
        try {
          const response = await axiosInstance.get(
            API_ENDPOINTS.PATIENTS.GET_BY_ID(constanteData.patientId)
          );

          if (response.status >= 200 && response.status < 300) {
            const patientData: Patient = response.data;
            setPatientDetails(patientData);

            // Mise à jour de formData.patientInfo avec les données récupérées
            updateFormData("patientInfo", "prenom", patientData.firstName || "");
            updateFormData("patientInfo", "nom", patientData.lastName || "");
            updateFormData("patientInfo", "dateNaissance", patientData.dateOfBirth || "");
            updateFormData("patientInfo", "telephone", patientData.phone || "");
            updateFormData("patientInfo", "adresse", patientData.address || "");
            updateFormData("patientInfo", "sexe", patientData.gender || "");
            updateFormData("patientInfo", "email", patientData.email || "");
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des données patient:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchPatientDetails();
  }, [constanteData?.patientId, constanteData?.patient, patientDetails, updateFormData]);

  // --- Calcul âge ---
  const calculateAge = (dateString: string): number | null => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    if (isNaN(birthDate.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // --- Extraction des données patient ---
  const getPatientData = (): ExtractedPatientData => {
    if (!constanteData) {
      return {
        fullName: "",
        firstName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        address: "",
        gender: "",
        email: "",
        motif: "",
        poids: "",
        taille: "",
        tension: "",
        rythme: "",
        temperature: "",
        age: null,
      };
    }

    const patient = patientDetails || constanteData.patient;

    const dateNaissance =
      info.dateNaissance ||
      (patient as any)?.dateNaissance ||
      patient?.dateOfBirth ||
      constanteData.dateOfBirth ||
      "";
    const age = calculateAge(dateNaissance);

    let fullName = "";
    if (constanteData.patientFullName) {
      fullName = constanteData.patientFullName;
    } else if (patient?.firstName && patient?.lastName) {
      fullName = `${patient.firstName} ${patient.lastName}`.trim();
    } else if (constanteData.firstName && constanteData.lastName) {
      fullName = `${constanteData.firstName} ${constanteData.lastName}`.trim();
    } else if (info.prenom && info.nom) {
      fullName = `${info.prenom} ${info.nom}`.trim();
    }

    const firstName = patient?.firstName || constanteData.firstName || info.prenom || "";
    const lastName = patient?.lastName || constanteData.lastName || info.nom || "";
    const phone = constanteData.patientPhone || patient?.phone || constanteData.phone || info.telephone || "";
    const address = patient?.address || constanteData.address || info.adresse || "";
    const gender = patient?.gender || constanteData.gender || info.sexe || "";
    const email = patient?.email || constanteData.email || info.email || "";

    let motif = constanteData.motif || "";
    if (!motif && constanteData.reason) {
      motif = reasonMap[constanteData.reason as VisitReason] || constanteData.reason;
    }

    const poids = constanteData.weight?.toString() || constanteData.poids?.toString() || "";
    const taille = constanteData.height?.toString() || constanteData.taille?.toString() || "";
    const tension = constanteData.bloodPressure || constanteData.tensionArterielle || "";
    const rythme = constanteData.heartRate?.toString() || constanteData.rythmeCardiaque?.toString() || constanteData.frequenceCardiaque?.toString() || "";
    const temperature = constanteData.temperature?.toString() || "";

    return {
      fullName,
      firstName,
      lastName,
      phone,
      dateOfBirth: dateNaissance,
      address,
      gender,
      email,
      motif,
      poids,
      taille,
      tension,
      rythme,
      temperature,
      age,
    };
  };

  const patientData = getPatientData();

  // --- Format affichage sexe ---
  const getGenderDisplay = (gender: string) => {
    if (!gender) return "N/A";
    const normalizedGender = gender.toLowerCase();
    switch (normalizedGender) {
      case "m":
      case "male":
      case "masculin":
      case "homme":
        return "Masculin";
      case "f":
      case "female":
      case "féminin":
      case "femme":
        return "Féminin";
      default:
        return gender;
    }
  };

  // --- Format date ---
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-blue-50 via-white to-teal-50 rounded-2xl p-6 shadow-lg border border-blue-100 animate-slideUp">
      {/* Floating medical icons */}
      <div className="absolute inset-0 overflow-hidden">
        {[
          { Icon: Heart, color: "text-red-200", top: "10%", left: "5%" },
          { Icon: Stethoscope, color: "text-blue-200", top: "20%", right: "5%" },
          { Icon: Heart, color: "text-pink-200", bottom: "15%", left: "10%" },
          { Icon: Stethoscope, color: "text-teal-200", bottom: "10%", right: "10%" },
        ].map(({ Icon, color, ...position }, i) => (
          <div key={i} className={`absolute ${color} opacity-40`} style={position}>
            <Icon className="w-6 h-6" />
          </div>
        ))}
      </div>

      <div className="relative z-10 bg-white/80 backdrop-blur-sm rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-800">
            Informations du Patient
          </h3>
          {loading && (
            <div className="flex items-center text-blue-600 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
              Chargement...
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Nom complet</label>
            <input
              type="text"
              value={patientData.fullName || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Téléphone</label>
            <input
              type="text"
              value={patientData.phone || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Date de naissance</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={formatDateForInput(patientData.dateOfBirth || "")}
                readOnly
                className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
              />
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Âge</label>
                <input
                  type="text"
                  value={patientData.age !== null ? `${patientData.age} ans` : "N/A"}
                  readOnly
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Adresse</label>
            <input
              type="text"
              value={patientData.address || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Sexe</label>
            <input
              type="text"
              value={getGenderDisplay(patientData.gender || "")}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Poids (kg)</label>
            <input
              type="text"
              value={patientData.poids || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Taille (cm)</label>
            <input
              type="text"
              value={patientData.taille || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Tension artérielle</label>
            <input
              type="text"
              value={patientData.tension || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Rythme cardiaque (bpm)</label>
            <input
              type="text"
              value={patientData.rythme || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Température (°C)</label>
            <input
              type="text"
              value={patientData.temperature || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="block text-sm font-medium text-slate-700">Motif de la consultation</label>
            <input
              type="text"
              value={patientData.motif || info.motifConsultation || "N/A"}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Date de consultation</label>
            <input
              type="date"
              value={formatDateForInput(info.dateConsultation) || formatDateForInput(new Date().toISOString())}
              readOnly
              className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-slate-700 outline-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientInfoStep;
